/**
 * Structured Logging System for Mobile
 *
 * Provides consistent logging across the mobile app with:
 * - Log levels: debug, info, warn, error
 * - Sentry integration for APM
 * - Console output in development
 * - Structured context support
 *
 * Sentry capture model:
 * - log.error() always creates a Sentry issue via captureException
 * - log.error(msg, error, ctx) captures the original error (rich stack trace; accepts unknown)
 * - log.error(msg, ctx) captures a new Error (stack includes caller chain)
 * - log.warn/info/debug only add breadcrumbs (no Sentry issue)
 *
 * Usage:
 *   import { createLogger } from '../utils/logger';
 *
 *   const log = createLogger('MyComponent');
 *   log.info('User action', { userId: '123' });
 *   log.error('Assessment failed', error, { userId: '123' });
 *   log.error('Timeout exceeded');
 */

import * as Sentry from '@sentry/react-native';

// ============================================
// Types
// ============================================

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

/**
 * Sentry tags surface as filterable facets on the issues list — fast
 * "how often did X fire?" queries without scanning issue titles.
 * The logger reads the special `_tags` field out of the context and
 * forwards its entries to `Sentry.captureException`'s `tags` option;
 * the rest of the context still goes into `extra`.
 *
 * Tag values must be primitives — Sentry rejects objects. Use this
 * for low-cardinality facets like timeout codes, error categories,
 * or `mode: send | regenerate`. NOT for things like sessionIds.
 *
 * @example
 *   log.error('Stream stalled', new Error('STREAM_STALLED'), {
 *     sessionId,
 *     _tags: { timeoutCode: 'STREAM_STALLED', mode: 'opening' },
 *   });
 */
interface LogContextWithTags extends LogContext {
  _tags?: Record<string, string | number | boolean | null | undefined>;
}

// ============================================
// Logger Implementation
// ============================================

class Logger {
  private component: string;

  constructor(component: string) {
    this.component = component;
  }

  /**
   * Log at debug level - detailed information for development
   * Filtered out in production logs, but still creates breadcrumbs
   */
  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }

  /**
   * Log at info level - normal operation milestones
   */
  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  /**
   * Log at warn level - recoverable issues (breadcrumb only, no Sentry issue).
   *
   * Accepts `unknown` for the error parameter so callers can pass catch
   * block errors directly. Error details are added to the breadcrumb context.
   *
   * @example
   * log.warn('Failed to load session', error, { sessionId });
   * log.warn('Retry limit reached', { attempt: 3 });
   */
  warn(message: string, errorOrContext?: unknown, context?: LogContext): void {
    const { error, ctx } = this.resolveErrorOrContext(errorOrContext, context);
    // warn never captures — error details go into breadcrumb context
    const enrichedCtx = error
      ? { ...ctx, error: error.message, errorName: error.name }
      : ctx;
    this.log('warn', message, enrichedCtx);
  }

  /**
   * Log at error level - errors that need attention.
   * Always creates a Sentry issue via captureException.
   *
   * Accepts `unknown` for the error parameter so callers can pass catch
   * block errors directly without an `instanceof` check. Non-Error values
   * are coerced to `new Error(String(value))`.
   *
   * @example
   * log.error('Assessment failed', error, { userId: '123' });
   * log.error('Voice recording timed out');
   * log.error('Config missing', { key: 'API_URL' });
   */
  error(message: string, errorOrContext?: unknown, context?: LogContext): void {
    const { error, ctx } = this.resolveErrorOrContext(errorOrContext, context);
    this.log('error', message, ctx, error ?? undefined);
  }

  /**
   * Disambiguate the overloaded (errorOrContext, context) parameters.
   * Returns a resolved Error (if any) and the LogContext.
   */
  private resolveErrorOrContext(
    errorOrContext: unknown,
    context?: LogContext,
  ): { error: Error | null; ctx: LogContext | undefined } {
    if (errorOrContext instanceof Error) {
      return { error: errorOrContext, ctx: context };
    }
    if (
      errorOrContext !== undefined &&
      errorOrContext !== null &&
      typeof errorOrContext !== 'object'
    ) {
      // Primitive non-Error value (string, number) — coerce to Error
      return { error: new Error(String(errorOrContext)), ctx: context };
    }
    // undefined, null, or plain object (LogContext)
    return { error: null, ctx: errorOrContext as LogContext | undefined };
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, context?: LogContext, captureError?: Error): void {
    const formatted = `[${this.component}] ${message}`;
    const timestamp = new Date().toISOString();

    // Development: console output
    if (__DEV__) {
      /* eslint-disable no-console -- logger is the sanctioned console wrapper */
      const consoleFn =
        level === 'error'
          ? console.error
          : level === 'warn'
            ? console.warn
            : console.log;
      /* eslint-enable no-console */

      // Format context for readability
      if (context && Object.keys(context).length > 0) {
        consoleFn(formatted, context);
      } else {
        consoleFn(formatted);
      }
    }

    // Sentry integration (when initialized)
    if (Sentry.getClient()) {
      // Pull `_tags` out of context — it gets forwarded to Sentry's
      // `tags` option separately so callers get filterable facets on
      // the issues list. The rest of the context stays as `extra`.
      const tags = (context as LogContextWithTags | undefined)?._tags;
      const extraCtx: LogContext = { component: this.component, timestamp };
      if (context) {
        for (const [k, v] of Object.entries(context)) {
          if (k !== '_tags') extraCtx[k] = v;
        }
      }

      // Add breadcrumb for all log levels
      Sentry.addBreadcrumb({
        message: formatted,
        category: this.component,
        level: this.mapLevelToSentry(level),
        data: extraCtx,
      });

      // Capture errors as Sentry issues
      if (level === 'error') {
        const captureOpts: { extra: LogContext; tags?: Record<string, string | number | boolean | null | undefined>; fingerprint?: string[] } = {
          extra: extraCtx,
        };
        if (tags) captureOpts.tags = tags;

        if (captureError) {
          // Original error provided → rich stack trace, grouped by error type + location
          Sentry.captureException(captureError, captureOpts);
        } else {
          // No error object → stack includes caller chain via new Error()
          // Fingerprint by [component, message] so each unique error gets its own issue
          captureOpts.fingerprint = [this.component, message];
          Sentry.captureException(new Error(formatted), captureOpts);
        }
      }
    }
  }

  /**
   * Map our log levels to Sentry severity levels
   */
  private mapLevelToSentry(
    level: LogLevel
  ): 'debug' | 'info' | 'warning' | 'error' {
    switch (level) {
      case 'debug':
        return 'debug';
      case 'info':
        return 'info';
      case 'warn':
        return 'warning';
      case 'error':
        return 'error';
    }
  }
}

// ============================================
// Factory Function
// ============================================

/**
 * Create a component-specific logger
 *
 * @param component - Name of the component/module for log prefixing
 * @returns Logger instance
 *
 * @example
 * const log = createLogger('ConversationStore');
 * log.info('Session loaded', { sessionId, messageCount: 5 });
 */
export function createLogger(component: string): Logger {
  return new Logger(component);
}

// ============================================
// Convenience Export
// ============================================

/**
 * Default logger for one-off logging
 * Prefer createLogger() for component-specific logging
 */
export const logger = new Logger('App');
